import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import {
  ArrowTrendingUpIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import ArrowTrendingDownIcon from "@heroicons/react/24/solid/ArrowTrendingDownIcon";
import { Loader2 } from "lucide-react";

interface MentorProfileInterestData {
  matches: number;
  skips: number;
  matchRatePercentage: number;
}
export interface ProfileInterestData {
  mentorProfileInterestData: MentorProfileInterestData;
}
export interface ProfileInterestDataProp {
  data?: {
    profileInterestData?: ProfileInterestData;
  };
}
export function MatchRate({
  profileInterestData,
  isLoading,
}: {
  profileInterestData: ProfileInterestDataProp | undefined;
  isLoading: boolean;
}) {
  const mentorProfileInterestData =
    profileInterestData?.data?.profileInterestData?.mentorProfileInterestData;

  const totalViews =
    (mentorProfileInterestData?.matches ?? 0) +
    (mentorProfileInterestData?.skips ?? 0);

  const conditionalRedOrGreenTag =
    (mentorProfileInterestData?.matchRatePercentage ?? 0) > 30;

  const matchRatePercentage = Math.floor(
    mentorProfileInterestData?.matchRatePercentage || 0,
  );

  return (
    <Card className={cn(isLoading && "animate-pulse")}>
      {isLoading ? (
        <div className="mb-4 flex items-center justify-center p-6">
          <Loader2 className="h-4 w-4 animate-spin" />
        </div>
      ) : (
        <CardContent className="p-6">
          <div className="mb-4 flex items-center gap-2">
            <h3 className="font-['TWK Luasanne'] font-normal text-gray-400">
              Match Rate
            </h3>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <InformationCircleIcon className="h-4 w-4 cursor-help text-gray-400 hover:text-gray-600" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs">
                    The percentage of times your profile was selected when
                    viewed by teens. A higher match rate indicates strong
                    profile appeal.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <div className="font-['TWK Luasanne'] mb-4 text-3xl font-bold text-gray-900">
            {matchRatePercentage}%
          </div>
          <div
            className={cn(
              "font-['TWK Luasanne'] flex items-center gap-1 rounded p-2 text-sm",
              conditionalRedOrGreenTag ? "text-green-500" : "text-red-500",
            )}
            style={{
              backgroundColor: conditionalRedOrGreenTag ? "#F7FFEE" : "#FFF1F0",
            }}
          >
            {conditionalRedOrGreenTag ? (
              <ArrowTrendingUpIcon className="h-4 w-4" />
            ) : (
              <ArrowTrendingDownIcon className="h-4 w-4" />
            )}
            <span>
              Profile was viewed {totalViews || 0} times and picked{" "}
              {mentorProfileInterestData?.matches || 0} times
            </span>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
